/* Primorys — tiroir panier maison (remplace Kaching Cart).
   - Compatible avec product-form.js du thème (getSectionsToRender / renderContents / open).
   - Ajoute / retire automatiquement l'e-book offert (propriété _primo_gift) selon le nombre de pots.
   - Les montants de remise viennent des réductions automatiques Shopify (via /cart.js). */
(() => {
  const configEl = document.getElementById('primo-cart-config');
  if (!configEl) return;
  const CONFIG = JSON.parse(configEl.textContent);
  // Sur /cart : élément de nettoyage caché, sinon le tiroir <cart-drawer> attendu par le thème.
  const TAG = CONFIG.isCartPage ? 'primo-cart-sync' : 'cart-drawer';
  if (customElements.get(TAG)) return;
  const T = CONFIG.texts;
  const TIMER_KEY = 'primoCartTimerEnd';
  const GIFT_PROP = '_primo_gift';

  // Fetch d'origine : nos propres appels ne déclenchent pas l'écouteur global plus bas.
  const nativeFetch = window.fetch.bind(window);

  const fill = (text, values) => text.replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));

  const formatMoney = (cents) => {
    const value = (Number(cents) || 0) / 100;
    const fmt = CONFIG.moneyFormat || '€{{amount_with_comma_separator}}';
    const withSep = (thousands, decimal, decimals = 2) => {
      const [int, dec] = value.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_with_comma_separator':
          return withSep('.', ',');
        case 'amount_no_decimals':
          return withSep(',', '.', 0);
        case 'amount_no_decimals_with_comma_separator':
          return withSep('.', ',', 0);
        case 'amount_with_apostrophe_separator':
          return withSep("'", '.');
        default:
          return withSep(',', '.');
      }
    });
  };

  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const imageUrl = (url, width) => {
    if (!url) return '';
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', width);
    return u.toString();
  };


  const svg = (d, extra = '') =>
    `<svg class="primo-cart__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"${extra}>${d}</svg>`;
  const ICONS = {
    truck: svg('<path d="M3 6h11v9H3zM14 9h4l3 3v3h-7z"/><circle cx="7" cy="17" r="1.8"/><circle cx="17" cy="17" r="1.8"/>'),
    gift: svg('<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M5 12v9h14v-9M12 8S10.5 3 8 3.5 7.5 8 12 8zM12 8s1.5-5 4-4.5S16.5 8 12 8z"/>'),
    tag: svg('<path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.3" fill="currentColor"/>'),
    trash: svg('<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"/>'),
    repeat: svg('<path d="M17 2l3 3-3 3M4 11V9a4 4 0 0 1 4-4h12M7 22l-3-3 3-3M20 13v2a4 4 0 0 1-4 4H4"/>'),
  };

  const isGiftLine = (item) => Boolean(item.variant_id === CONFIG.giftVariantId && item.properties && item.properties[GIFT_PROP]);
  const isMainLine = (item) => item.product_id === CONFIG.mainProductId;

  class PrimoCart extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.primo-cart__panel');
      this.titleEl = this.querySelector('.primo-cart__title');
      this.announcementEl = this.querySelector('.primo-cart__announcement');
      this.tiersEl = this.querySelector('.primo-cart__tiers');
      this.itemsEl = this.querySelector('.primo-cart__items');
      this.emptyEl = this.querySelector('.primo-cart__empty');
      this.footerEl = this.querySelector('.primo-cart__footer');
      this.discountsRow = this.querySelector('.primo-cart__row--discounts');
      this.discountsEl = this.querySelector('.primo-cart__discounts-amount');
      this.discountBadgesEl = this.querySelector('.primo-cart__discount-badges');
      this.subtotalEl = this.querySelector('.primo-cart__subtotal-amount');
      this.checkoutEl = this.querySelector('.primo-cart__checkout');

      this.querySelectorAll('[data-primo-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
      this.itemsEl.addEventListener('click', (event) => this.onItemsClick(event));
      this.itemsEl.addEventListener('change', (event) => this.onQuantityInput(event));

      if (!CONFIG.isCartPage) this.bindCartIcon();
      this.watchExternalCartChanges();
      this.refresh();
    }

    /* ---------- API attendue par le thème ---------- */

    getSectionsToRender() {
      return [{ id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      this.updateBubbleFromSections(parsedState && parsedState.sections);
      this.resetTimer();
      this.refresh().then(() => this.open());
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('is-open');
      document.documentElement.classList.add('primo-cart-open');
      this.startTimer();
      // Laisse le tiroir finir de glisser avant de faire avancer la barre.
      clearTimeout(this.openAnimTimer);
      this.openAnimTimer = setTimeout(() => this.animateTiers(), 350);
      setTimeout(() => this.panel.focus?.({ preventScroll: true }), 50);
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('primo-cart-open');
      if (this.activeElement && this.activeElement.focus) this.activeElement.focus({ preventScroll: true });
    }

    /* ---------- Données ---------- */

    // Les rafraîchissements sont mis en file : deux appels simultanés ne peuvent pas ajouter l'e-book deux fois.
    refresh() {
      this.queue = (this.queue || Promise.resolve()).then(() => this.doRefresh());
      return this.queue;
    }

    async doRefresh() {
      try {
        const before = await this.fetchCart();
        const cart = await this.syncGift(before);
        // Sur la page /cart, le contenu affiché vient du thème : on recharge si le nettoyage a changé le panier.
        if (CONFIG.isCartPage && cart !== before) {
          window.location.reload();
          return cart;
        }
        this.cart = cart;
        this.render(cart);
        return cart;
      } catch (error) {
        console.error('[Primo Cart]', error);
        this.itemsEl.innerHTML = `<p class="primo-cart__error">${escapeHtml(T.error)}</p>`;
      }
    }

    async fetchCart() {
      const res = await nativeFetch(`${CONFIG.rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    }

    async post(path, body) {
      const res = await nativeFetch(`${CONFIG.rootUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
      return data;
    }

    // Ajoute l'e-book offert dès 3 pots, le retire en dessous. Ne touche jamais un e-book acheté normalement.
    async syncGift(cart) {
      // Paniers restés de l'époque Kaching : leur e-book "cadeau" n'est plus offert, on le retire.
      const legacyGifts = cart.items.filter((item) => item.properties && item.properties._kaching_cart);
      if (legacyGifts.length) {
        const updates = {};
        legacyGifts.forEach((line) => (updates[line.key] = 0));
        await this.post('cart/update.js', { updates });
        cart = await this.fetchCart();
      }

      const mainQty = cart.items.filter(isMainLine).reduce((sum, item) => sum + item.quantity, 0);
      const giftLines = cart.items.filter(isGiftLine);
      const shouldHaveGift = mainQty >= CONFIG.giftThreshold;

      if (shouldHaveGift && giftLines.length === 0) {
        await this.post('cart/add.js', { items: [{ id: CONFIG.giftVariantId, quantity: 1, properties: { [GIFT_PROP]: '1' } }] });
        return this.fetchCart();
      }

      const extra = shouldHaveGift ? giftLines.slice(1) : giftLines;
      const fixQty = shouldHaveGift && giftLines[0] && giftLines[0].quantity !== 1 ? giftLines[0] : null;
      if (extra.length || fixQty) {
        const updates = {};
        extra.forEach((line) => (updates[line.key] = 0));
        if (fixQty) updates[fixQty.key] = 1;
        await this.post('cart/update.js', { updates });
        return this.fetchCart();
      }
      return cart;
    }

    async changeQuantity(key, quantity) {
      this.classList.add('is-loading');
      try {
        const keys = String(key).split(',');
        if (keys.length > 1) {
          const updates = {};
          keys.forEach((k, i) => (updates[k] = i === 0 ? quantity : 0));
          await this.post('cart/update.js', { updates });
        } else {
          await this.post('cart/change.js', { id: key, quantity });
        }
        this.resetTimer();
      } catch (error) {
        this.flashError(error.data?.description || T.inventory);
      }
      await this.refresh();
      this.refreshBubble();
      this.classList.remove('is-loading');
    }

    /* ---------- Rendu ---------- */

    render(cart) {
      const empty = cart.item_count === 0;
      this.classList.toggle('is-empty', empty);
      this.titleEl.textContent = fill(T.title, { item_count: cart.item_count });
      this.emptyEl.hidden = !empty;
      this.footerEl.hidden = empty;
      this.announcementEl.hidden = empty;
      this.tiersEl.hidden = empty;

      if (empty) {
        this.itemsEl.innerHTML = '';
        this.tierEls = null;
        try { localStorage.removeItem(TIMER_KEY); } catch (e) {}
        return;
      }

      this.renderTiers(cart);
      this.renderItems(cart);
      this.renderFooter(cart);
      this.startTimer();
    }

    // La barre est construite une seule fois puis mise à jour : sa largeur peut ainsi s'animer
    // depuis la position précédente, et chaque palier s'allume au moment où la barre l'atteint.
    renderTiers(cart) {
      const count = cart.items.filter((i) => !isGiftLine(i)).reduce((sum, i) => sum + i.quantity, 0);
      const tiers = CONFIG.tiers;
      const n = tiers.length;
      const nextIndex = tiers.findIndex((t) => t.threshold > count);
      const next = tiers[nextIndex];
      const text = next
        ? fill(T.tierDefault, { progress_needed: next.threshold - count, next_promo_title: next.title })
        : T.tierAllUnlocked;
      // Étapes espacées régulièrement ; la barre progresse proportionnellement entre deux paliers.
      let progress = 100;
      if (next) {
        const prev = nextIndex > 0 ? tiers[nextIndex - 1].threshold : 0;
        progress = ((nextIndex + (count - prev) / (next.threshold - prev)) / n) * 100;
      }

      if (!this.tierEls) {
        this.tiersEl.innerHTML = `
          <p class="primo-cart__tiers-text"></p>
          <div class="primo-cart__tiers-track">
            <div class="primo-cart__tiers-fill" style="width:0%"></div>
            ${tiers.map((t, i) => `<span class="primo-cart__tier-step" style="left:${((i + 1) / n) * 100}%"></span>`).join('')}
          </div>
          <div class="primo-cart__tiers-labels">
            ${tiers
              .map(
                (t, i) => `<div class="primo-cart__tier-label${i === n - 1 ? ' is-last' : ''}" style="left:${((i + 1) / n) * 100}%">
                  ${ICONS[t.icon] || ''}
                  <span><span class="primo-cart__tier-check">✅ </span>${escapeHtml(t.title)}</span>
                </div>`
              )
              .join('')}
          </div>`;
        this.tierEls = {
          text: this.tiersEl.querySelector('.primo-cart__tiers-text'),
          fill: this.tiersEl.querySelector('.primo-cart__tiers-fill'),
          steps: [...this.tiersEl.querySelectorAll('.primo-cart__tier-step')],
          labels: [...this.tiersEl.querySelectorAll('.primo-cart__tier-label')],
        };
        this.shownProgress = 0;
      }

      this.tierEls.text.textContent = text;
      this.tierEls.text.classList.toggle('is-complete', !next);
      this.targetProgress = progress;
      this.animateTiers();
    }

    // Anime la barre vers targetProgress, seulement quand le tiroir est ouvert (sinon on ne verrait rien).
    animateTiers() {
      if (!this.tierEls || !this.classList.contains('is-open')) return;
      const from = this.shownProgress;
      const to = this.targetProgress;
      const n = CONFIG.tiers.length;
      const { fill, steps, labels } = this.tierEls;
      const duration = Math.max(400, Math.abs(to - from) * 14); // ~1,4 s pour une barre complète

      (this.tierTimers || []).forEach(clearTimeout);
      this.tierTimers = [];

      fill.style.transitionDuration = `${duration}ms`;
      void fill.offsetWidth; // fixe la largeur de départ pour que la transition parte bien de là
      fill.style.width = `${to}%`;
      this.shownProgress = to;

      steps.forEach((step, i) => {
        const pos = ((i + 1) / n) * 100;
        const unlocked = to >= pos - 0.01;
        const label = labels[i];
        if (!unlocked) {
          step.classList.remove('is-unlocked', 'is-pop');
          label.classList.remove('is-unlocked', 'is-pop');
          return;
        }
        if (step.classList.contains('is-unlocked')) return;
        // S'allume à l'instant où la barre atteint le carré.
        const delay = to > from ? ((pos - from) / (to - from)) * duration : 0;
        this.tierTimers.push(
          setTimeout(() => {
            step.classList.add('is-unlocked', 'is-pop');
            label.classList.add('is-unlocked', 'is-pop');
            setTimeout(() => {
              step.classList.remove('is-pop');
              label.classList.remove('is-pop');
            }, 700);
          }, Math.max(0, delay))
        );
      });
    }

    // Regroupe les lignes d'une même variante (Shopify les coupe quand une offre "achetez X obtenez Y" s'applique)
    // et reporte sur le produit principal les remises au niveau de la commande, comme Kaching.
    buildGroups(cart) {
      const cartLevel = cart.cart_level_discount_applications || [];
      const cartLevelTotal = cartLevel.reduce((sum, d) => sum + d.total_allocated_amount, 0);
      const groups = [];
      const byVariant = {};
      cart.items.forEach((item) => {
        if (isGiftLine(item)) {
          groups.push({ gift: true, item, keys: [item.key], saved: item.original_line_price - item.final_line_price });
          return;
        }
        let g = byVariant[item.variant_id];
        if (!g) {
          g = byVariant[item.variant_id] = {
            gift: false, item, keys: [], quantity: 0, original: 0, final: 0, titles: new Set(), main: isMainLine(item),
          };
          groups.push(g);
        }
        g.keys.push(item.key);
        g.quantity += item.quantity;
        g.original += item.original_line_price;
        g.final += item.final_line_price;
        (item.line_level_discount_allocations || []).forEach((a) => a.amount > 0 && g.titles.add(a.discount_application.title.trim()));
      });
      const main = groups.find((g) => g.main);
      if (main && cartLevelTotal > 0) {
        main.final -= cartLevelTotal;
        cartLevel.forEach((d) => main.titles.add(d.title.trim()));
      }
      groups.forEach((g) => { if (!g.gift) g.saved = g.original - g.final; });
      // Les cadeaux s'affichent toujours sous les produits achetés.
      return groups.sort((x, y) => x.gift - y.gift);
    }

    renderItems(cart) {
      const groups = this.buildGroups(cart);
      const mainQty = groups.filter((g) => g.main).reduce((sum, g) => sum + g.quantity, 0);

      this.itemsEl.innerHTML = groups
        .map((g) => {
          const item = g.item;
          const keys = g.keys.join(',');

          if (g.gift) {
            return `<div class="primo-cart__gift" data-key="${item.key}">
              <span class="primo-cart__gift-title">${ICONS.gift}${escapeHtml(item.product_title)}</span>
              <span class="primo-cart__gift-prices">
                ${g.saved > 0 ? `<s class="primo-cart__compare">${formatMoney(item.original_line_price)}</s>` : ''}
                <span class="primo-cart__price">${item.final_line_price === 0 ? escapeHtml(T.free) : formatMoney(item.final_line_price)}</span>
              </span>
            </div>`;
          }

          const badges = [...g.titles].map((t) => `<span class="primo-cart__badge">${ICONS.tag}${escapeHtml(t)}</span>`).join('');

          let upgrade = '';
          if (g.main) {
            const step = CONFIG.bundleSteps.find((s) => s.quantity > mainQty);
            if (step) {
              const left = step.quantity - mainQty;
              upgrade = `<button type="button" class="primo-cart__upgrade" data-upgrade="${keys}" data-target="${g.quantity + left}">${escapeHtml(
                fill(T.bundleUpgrade, { quantity_left_to_next_bar: left, next_bar_saved_amount: formatMoney(step.saved) })
              )}</button>`;
            }
          }

          const variant = item.variant_title && !/default/i.test(item.variant_title) ? `<span class="primo-cart__variant">${escapeHtml(item.variant_title)}</span>` : '';
          const img = item.image ? `<img src="${imageUrl(item.image, 160)}" alt="" width="80" height="80" loading="lazy">` : '';

          return `<div class="primo-cart__item" data-key="${keys}">
            <a class="primo-cart__item-image" href="${item.url}">${img}</a>
            <div class="primo-cart__item-info">
              <a class="primo-cart__item-title" href="${item.url}">${g.quantity}x ${escapeHtml(item.product_title)}</a>
              ${variant}
              ${badges ? `<div class="primo-cart__badges">${badges}</div>` : ''}
              <div class="primo-cart__qty">
                <button type="button" data-qty-step="-1" data-key="${keys}" aria-label="-">−</button>
                <input type="number" min="0" value="${g.quantity}" data-key="${keys}" aria-label="Quantité">
                <button type="button" data-qty-step="1" data-key="${keys}" aria-label="+">+</button>
              </div>
              ${upgrade}
            </div>
            <div class="primo-cart__item-side">
              ${g.saved > 0 ? `<s class="primo-cart__compare">${formatMoney(g.original)}</s>` : ''}
              <span class="primo-cart__price">${formatMoney(g.final)}</span>
              ${g.saved > 0 ? `<span class="primo-cart__savings">${ICONS.tag}<span>${escapeHtml(fill(T.youSave, { amount_saved: formatMoney(g.saved) }))}</span></span>` : ''}
              <button type="button" class="primo-cart__remove" data-remove="${keys}" aria-label="${escapeHtml(T.remove)}">${ICONS.trash}</button>
            </div>
          </div>`;
        })
        .join('');
    }

    renderFooter(cart) {
      const lineDiscounts = cart.items.reduce((sum, i) => sum + (i.original_line_price - i.final_line_price), 0);
      const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + d.total_allocated_amount, 0);
      const discounts = lineDiscounts + cartDiscounts;

      // Une pastille par réduction appliquée (titres dédoublonnés).
      const titles = new Set();
      cart.items.forEach((i) =>
        (i.line_level_discount_allocations || []).forEach((a) => a.amount > 0 && titles.add(a.discount_application.title.trim()))
      );
      (cart.cart_level_discount_applications || []).forEach((d) => titles.add(d.title.trim()));

      this.discountsRow.hidden = discounts <= 0;
      this.discountBadgesEl.innerHTML = [...titles].map((t) => `<span class="primo-cart__pill">${ICONS.tag}${escapeHtml(t)}</span>`).join('');
      this.discountsEl.textContent = `-${formatMoney(discounts)}`;
      this.subtotalEl.textContent = formatMoney(cart.total_price);
      this.checkoutEl.textContent = fill(T.checkout, { cart_total: formatMoney(cart.total_price) });
    }

    /* ---------- Interactions ---------- */

    onItemsClick(event) {
      const step = event.target.closest('[data-qty-step]');
      const remove = event.target.closest('[data-remove]');
      const upgrade = event.target.closest('[data-upgrade]');
      const subscribe = event.target.closest('[data-subscribe]');
      if (step) {
        const input = this.itemsEl.querySelector(`input[data-key="${step.dataset.key}"]`);
        const qty = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
        this.changeQuantity(step.dataset.key, qty);
      } else if (remove) {
        this.changeQuantity(remove.dataset.remove, 0);
      } else if (upgrade) {
        this.changeQuantity(upgrade.dataset.upgrade, Number(upgrade.dataset.target));
      } else if (subscribe) {
        this.changeSellingPlan(subscribe.dataset.subscribe, CONFIG.sellingPlans[0].id);
      }
    }

    async changeSellingPlan(key, planId) {
      const item = this.cart.items.find((i) => i.key === key);
      if (!item) return;
      this.classList.add('is-loading');
      try {
        await this.post('cart/change.js', { id: key, quantity: item.quantity, selling_plan: planId || null });
      } catch (error) {
        this.flashError(error.data?.description || T.error);
      }
      await this.refresh();
      this.classList.remove('is-loading');
    }

    onQuantityInput(event) {
      const plan = event.target.closest('select[data-plan]');
      if (plan) return this.changeSellingPlan(plan.dataset.plan, Number(plan.value) || null);
      const input = event.target.closest('input[data-key]');
      if (!input) return;
      this.changeQuantity(input.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
    }

    flashError(message) {
      const el = document.createElement('p');
      el.className = 'primo-cart__error';
      el.textContent = message;
      this.itemsEl.prepend(el);
      setTimeout(() => el.remove(), 4000);
    }

    /* ---------- Minuteur de réservation ---------- */

    resetTimer() {
      try { localStorage.setItem(TIMER_KEY, String(Date.now() + CONFIG.timerMinutes * 60000)); } catch (e) {}
    }

    startTimer() {
      if (this.timerInterval) return;
      const tick = () => {
        if (this.classList.contains('is-empty')) return;
        let end;
        try { end = Number(localStorage.getItem(TIMER_KEY)); } catch (e) {}
        if (!end || end < Date.now()) {
          this.resetTimer();
          end = Date.now() + CONFIG.timerMinutes * 60000;
        }
        const left = Math.max(0, Math.round((end - Date.now()) / 1000));
        const mm = String(Math.floor(left / 60)).padStart(2, '0');
        const ss = String(left % 60).padStart(2, '0');
        const parts = T.announcement.split('[timer]');
        this.announcementEl.innerHTML = `${escapeHtml(parts[0])}<strong>${mm}:${ss}</strong>${escapeHtml(parts[1] || '')}`;
      };
      tick();
      this.timerInterval = setInterval(tick, 1000);
    }

    /* ---------- Icône panier du header ---------- */

    bindCartIcon() {
      const bind = (link) => {
        if (!link || link.dataset.primoBound) return;
        link.dataset.primoBound = '1';
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(link);
        });
      };
      bind(document.getElementById('cart-icon-bubble'));
      document.querySelectorAll('a[href$="/cart"]').forEach((a) => {
        if (a.closest('header, .header, .section-header')) bind(a);
      });
    }

    updateBubbleFromSections(sections) {
      const html = sections && sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }

    async refreshBubble() {
      try {
        const res = await nativeFetch(`${window.location.pathname}?sections=cart-icon-bubble`);
        this.updateBubbleFromSections(await res.json());
      } catch (e) {}
    }

    // Toute modification du panier faite par un autre script (upsell, quick add…) rafraîchit le tiroir.
    watchExternalCartChanges() {
      const self = this;
      let timeout;
      const schedule = () => {
        clearTimeout(timeout);
        timeout = setTimeout(async () => {
          await self.refresh();
          self.refreshBubble();
        }, 300);
      };
      window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
        const promise = nativeFetch(...args);
        if (url && /\/cart\/(add|change|update|clear)/.test(url)) {
          promise.then(() => schedule()).catch(() => {});
        }
        return promise;
      };
      document.addEventListener('cart:refresh', schedule);
    }
  }

  customElements.define(TAG, PrimoCart);
})();
